import { ManagedUserRoles } from "@redotech/account-common/roles";
import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  NotificationBannerItem,
  RedoNotificationBanner,
} from "@redotech/redo-web/arbiter-components/notification/redo-notification-banner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { OrderManagementSuiteStatusesContext } from "../../../redo-suite/order-management-cloud";

export const UspsHazmatFeeBanner = memo(function UspsHazmatFeeBanner() {
  const [dismissed, setDismissed] = useLocalStorage<boolean>(
    "usps-hazmat-fee-2026-dismissed",
    false,
  );
  const [modalOpen, setModalOpen] = useState(false);

  const orderManagementStatuses = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const user = useContext(UserContext);
  const routes = useRequiredContext(MerchantRoutesContext);
  const navigate = useNavigate();

  const isAdmin =
    user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ?? false;

  const isPastEffectiveDate = Date.now() > new Date("2026-07-12").getTime();

  if (
    dismissed ||
    !isAdmin ||
    !orderManagementStatuses.omsEnabled ||
    isPastEffectiveDate
  ) {
    return null;
  }

  const bannerItems: NotificationBannerItem[] = [
    {
      id: "usps-hazmat-fee-2026",
      type: "info",
      message:
        "USPS is adding new HAZMAT fees effective July 12, 2026. Ground Advantage shippers are exempt from the handling fee for now.",
      actions: [
        {
          actionType: "button",
          hierarchy: "tertiary",
          text: "Learn more",
          onClick: () => setModalOpen(true),
        },
      ],
    },
  ];

  return (
    <>
      <RedoNotificationBanner
        items={bannerItems}
        onDismiss={() => setDismissed(true)}
      />
      <RedoModal
        isOpen={modalOpen}
        modalSize="md"
        onModalCloseRequested={() => setModalOpen(false)}
        primaryButton={{
          text: "Go to automations",
          onClick: () => {
            setModalOpen(false);
            navigate(routes.getSettingsSubRoute("oms/automations"));
          },
        }}
        secondaryButton={{ text: "Close", onClick: () => setModalOpen(false) }}
        title="USPS HAZMAT Fee Update — Effective July 12, 2026"
      >
        <Flex dir="column" gap="lg">
          <Text fontSize="sm" textColor="secondary">
            Starting July 12, USPS is introducing two new fees for domestic
            packages containing hazardous materials (HAZMAT).
          </Text>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Handling Fee ($7.50)
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Charged at the time of mailing. Applies to Priority Mail and
              Priority Mail Express. Ground Advantage and Parcel Select are
              exempt for now, though USPS has indicated this may change.
            </Text>
          </Flex>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              Noncompliance Fee ($50)
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Applies to HAZMAT packages with incorrect or mismatched Service
              Type Code (STC), Service Icon, Barcode Banner Text, or DOT
              markings. This fee does apply to Ground Advantage, so proper
              HAZMAT labeling still matters even if you're exempt from the
              handling fee. Packages that are both noncompliant and missing the
              handling fee may be charged both.
            </Text>
          </Flex>
          <Flex dir="column" gap="xs">
            <Text fontSize="sm" fontWeight="semibold" textColor="primary">
              What to do
            </Text>
            <Text fontSize="sm" textColor="secondary">
              Make sure any HAZMAT shipments are properly identified with the
              correct STC, service icons, barcode banner text, and DOT markings.
              You can automate this in Redo for your HAZMAT products under
              Settings &gt; Order Management &gt; Automations using "Set
              shipping options." Or just use Ask Redo to create the automation
              for you.
            </Text>
          </Flex>
        </Flex>
      </RedoModal>
    </>
  );
});
